import { useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import { Stack, Typography } from '@mui/material';
import AddFileIcon from 'icons/AddFileIcon';
import SearchResultIcon from 'icons/SearchResultIcon';
import { useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';
import {
  getActiveFilters,
  getAdvancedFilters,
  getApprovedFilesForDashboard,
  getArchivedForDashboard,
  getSearchString,
  getStorageUnits,
  getTemplatesForDashboard,
} from 'store/storageunits/selectors';
import type { Filter, StorageUnit } from 'store/storageunits/types';
import { routes } from 'util/routes';
import { ReactComponent as TrashcanIcon } from 'icons/trashcan.svg';

const StyledStack = styled(Stack)`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  justify-content: center;
  align-items: center;
  background: white;
`;

export const IconWrapper = styled('div')<{ position: [number, number] }>`
  position: relative;
  background: ${({ theme }) => theme.palette.grey[200]};
  border-radius: 50%;
  width: 100px;
  height: 100px;
  margin-bottom: ${({ theme }) => theme.spacing(2)};
  color: #6f6f6f;

  > svg {
    position: absolute;
    left: 50%;
    top: 50%;
    transform: ${({ position }) =>
      `translate(-${position[0]}%, -${position[1]}%)`};
  }
`;

const Trashcan = styled(TrashcanIcon)`
  width: 64px;
  height: 64px;
`;

const getContent = (
  folder: string | undefined,
  filters: Filter[],
  hasAdvancedFilters: boolean,
  searchString: string | undefined,
  route: string,
): {
  title: string;
  text: string;
  icon: React.ReactElement;
  translate: [number, number];
} => {
  if (searchString !== '' || filters.length > 0 || hasAdvancedFilters) {
    return {
      title: 'None of the files and folders matched the search',
      text: 'Try refining your search terms or take a look at other categories for more options.',
      icon: <SearchResultIcon />,
      translate: [50, 50],
    };
  }

  if (route === routes.bin) {
    return {
      title: 'Bin is empty',
      text: 'All files moved to the bin will be shown here',
      icon: <Trashcan />,
      translate: [50, 50],
    };
  }

  return {
    title: folder === undefined ? "Let's get started!" : 'This folder is empty',
    text: "Click the 'New' button to create new files or drag and drop .ngpd files.",
    icon: <AddFileIcon />,
    translate: [55, 55],
  };
};

type Props = {
  onContextMenu: (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
};

const EmptyFileArea = ({ onContextMenu }: Props) => {
  const location = useLocation();
  const units = useSelector(getStorageUnits);
  const templates = useSelector(getTemplatesForDashboard);
  const approved = useSelector(getApprovedFilesForDashboard);
  const archived = useSelector(getArchivedForDashboard);

  let storageUnits: StorageUnit[] = [];
  if (location.pathname === routes.templates) {
    storageUnits = templates;
  } else if (location.pathname === routes.approvedFiles) {
    storageUnits = approved;
  } else if (location.pathname === routes.bin) {
    storageUnits = archived;
  } else {
    storageUnits = units;
  }

  const searchString = useSelector(getSearchString);
  const filters = useSelector(getActiveFilters);
  const advancedFilters = useSelector(getAdvancedFilters);

  const { id } = useParams<{ id?: string }>();
  const theme = useTheme();

  if (storageUnits.length !== 0) {
    return null;
  }

  const { title, text, icon, translate } = getContent(
    id,
    filters,
    Boolean(advancedFilters.country) || Boolean(advancedFilters.application),
    searchString,
    location.pathname,
  );

  return (
    <StyledStack onContextMenu={onContextMenu}>
      <IconWrapper position={translate}>{icon}</IconWrapper>
      <Stack
        sx={{
          width: '50%',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Typography
          sx={{
            color: theme.palette.grey[700],
            fontSize: 16,
            fontWeight: 700,
          }}
        >
          {title}
        </Typography>
        <Typography
          sx={{
            color: theme.palette.grey[700],
            fontSize: 14,
            textAlign: 'center',
            fontWeight: 400,
          }}
        >
          {text}
        </Typography>
      </Stack>
    </StyledStack>
  );
};

export default EmptyFileArea;
